import React from "react";

import * as Types from "../../TaskBoard/types";
import { SecondaryButton } from "../../Button";
import { BlackLink } from "../../Link";
import { PieChart } from "../../PieChart";
import { IconLayoutKanban, IconPlus } from "../../icons";
import { Tooltip } from "../../Tooltip";
import { useBoardDnD } from "../../utils/PragmaticDragAndDrop";
import type { BoardMove } from "../../utils/PragmaticDragAndDrop";
import { MilestonePage } from "..";
import { calculateMilestoneStats } from "../../TaskBoard/components/MilestoneCard";
import { TaskFilter } from "../../TaskBoard";
import { FilterBadges } from "../../TaskBoard/components/TaskFilter";
import TaskList from "../../TaskBoard/components/TaskList";
import { InlineTaskCreator } from "../../TaskBoard/components/InlineTaskCreator";
import { useInlineTaskCreator } from "../../TaskBoard/hooks/useInlineTaskCreator";
import { sortTasks } from "../../TaskBoard/utils/sortTasks";

export function TasksSection({
  tasks,
  filters,
  onFiltersChange,
  onTaskReorder,
  milestone,
  onTaskCreate,
  onTaskAssigneeChange,
  onTaskDueDateChange,
  onTaskStatusChange,
  assigneePersonSearch,
  setIsTaskModalOpen,
  statusOptions,
}: MilestonePage.State) {
  const {
    open: creatorOpen,
    openCreator,
    closeCreator,
    creatorRef,
    hoverBind,
  } = useInlineTaskCreator({
    requireHover: false,
  });
  const stats = calculateMilestoneStats(tasks);
  const completionPercentage = calculateCompletionPercentage(stats);

  const applyFilters = (tasks: Types.Task[], filters: Types.FilterCondition[]) => {
    return tasks.filter((task) => {
      return filters.every((filter) => {
        switch (filter.type) {
          case "status":
            if (filter.operator === "is") {
              if (!task.status || !filter.value) return false;
              return task.status.value === (filter.value as Types.Status).value;
            } else {
              if (!task.status || !filter.value) return true;
              return task.status.value !== (filter.value as Types.Status).value;
            }
          case "assignee":
            const hasAssignee = task.assignees?.some((assignee) => assignee.id === filter.value?.id);
            return filter.operator === "is" ? hasAssignee : !hasAssignee;
          case "content":
            const searchTerm = filter.value?.toLowerCase() || "";
            const taskContent = `${task.title} ${task.description || ""}`.toLowerCase();
            return filter.operator === "contains"
              ? taskContent.includes(searchTerm)
              : !taskContent.includes(searchTerm);
          default:
            return true;
        }
      });
    });
  };

  // Filter tasks based on current filters
  const baseFilteredTasks = applyFilters(tasks, filters || []);
  const orderedTasks = React.useMemo(
    () => sortTasks(baseFilteredTasks, milestone),
    [baseFilteredTasks, milestone],
  );

  const hasHiddenTasks = baseFilteredTasks.some((task) => task.status?.closed === true);

  const handleTaskMove = React.useCallback(
    (move: BoardMove) => {
      if (onTaskReorder) {
        onTaskReorder(move.itemId, move.destination.containerId, move.destination.index);
      }
    },
    [onTaskReorder],
  );

  const { draggedItemId, destination, draggedItemDimensions } = useBoardDnD(handleTaskMove);

  // Hotkey handled by useInlineTaskCreator

  return (
    <div className="space-y-4 pt-6" data-test-id="tasks-section" {...hoverBind}>
      {/* Task header container - visually groups all task-related controls */}
      <div className="bg-surface-dimmed rounded-lg border border-surface-outline">
        {/* Header bar with title, pie chart, and primary action */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-outline">
          <div className="flex items-center gap-3">
            <div className="h-4 w-6 flex items-center justify-center">
              <PieChart
                size={24}
                slices={[
                  {
                    percentage: completionPercentage,
                    color: "var(--color-callout-success-content)",
                  },
                ]}
              />
            </div>
            <h2 className="font-bold">Tasks</h2>
          </div>
          <div className="flex items-center gap-4">
            {milestone.kanbanLink && (
              <Tooltip content="View on board" size="sm">
                <BlackLink
                  to={milestone.kanbanLink}
                  className="flex items-center text-content-dimmed md:hover:text-content-base transition-colors"
                  underline="hover"
                >
                  <IconLayoutKanban size={18} className="text-content-dimmed" />
                  <span className="sr-only">View on board</span>
                </BlackLink>
              </Tooltip>
            )}

            <SecondaryButton size="xs" icon={IconPlus} onClick={openCreator} testId="tasks-section-add-task">
              <span className="sr-only">Add task</span>
            </SecondaryButton>
          </div>
        </div>

        {/* Filter controls */}
        {onFiltersChange && filters && (
          <div className="flex items-center gap-3 px-4 py-2 border-b border-surface-outline">
            <TaskFilter filters={filters} onFiltersChange={onFiltersChange} tasks={tasks} />
            {filters?.length > 0 && <FilterBadges filters={filters} onFiltersChange={onFiltersChange} />}
          </div>
        )}

        {/* Task list content */}
        <div className="bg-surface-base rounded-b-lg overflow-hidden">
          {baseFilteredTasks.length === 0 ? (
            /* Empty state with inline creation */
            <div className="px-4 py-6">
              {creatorOpen ? (
                <>
                  <InlineTaskCreator
                    ref={creatorRef}
                    milestone={milestone}
                    onCreate={(t) => {
                      onTaskCreate?.({ ...t, milestone });
                    }}
                    onRequestAdvanced={() => setIsTaskModalOpen(true)}
                    onCancel={closeCreator}
                    autoFocus
                    testId="inline-task-creator-milestonepage-empty"
                  />
                  <div className="px-0 pt-2 text-center text-content-subtle text-xs">
                    Press Enter to add. You can also drag tasks here.
                  </div>
                </>
              ) : (
                <div className="text-center text-content-subtle text-sm">
                  Click + or press c to add a task, or drag a task here.
                </div>
              )}
            </div>
          ) : (
            /* Task list with drag and drop */
            <TaskList
              tasks={orderedTasks}
              showHiddenTasksToggle={hasHiddenTasks}
              milestoneId={milestone.id}
              onTaskAssigneeChange={onTaskAssigneeChange}
              onTaskDueDateChange={onTaskDueDateChange}
              onTaskStatusChange={onTaskStatusChange}
              assigneePersonSearch={assigneePersonSearch}
              statusOptions={statusOptions}
              draggedItemId={draggedItemId}
              targetLocation={destination}
              placeholderHeight={draggedItemDimensions?.height ?? null}
              inlineCreateRow={
                creatorOpen ? (
                  <InlineTaskCreator
                    ref={creatorRef}
                    milestone={milestone}
                    onCreate={(t) => onTaskCreate?.({ ...t, milestone })}
                    onRequestAdvanced={() => setIsTaskModalOpen(true)}
                    onCancel={closeCreator}
                    autoFocus
                    testId="inline-task-creator-milestonepage"
                  />
                ) : undefined
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

function calculateCompletionPercentage(stats: {
  pending: number;
  inProgress: number;
  done: number;
  canceled: number;
  total: number;
}) {
  // Active tasks are those that aren't canceled
  const activeTasks = stats.total - stats.canceled;

  // If there are no active tasks, show as 0% complete
  if (activeTasks === 0) return 0;

  // Calculate percentage based only on active tasks
  return (stats.done / activeTasks) * 100;
}
